import dayjs from 'dayjs'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import updateLocale from 'dayjs/plugin/updateLocale'
import weekday from 'dayjs/plugin/weekday'
import 'dayjs/locale/pt-br'
import { ptBR } from '@mui/x-date-pickers/locales'

// Locale setup at module level: dayjs é singleton, chamar dentro do componente mutava o estado global a cada render.
dayjs.extend(weekday)
dayjs.extend(updateLocale)
dayjs.updateLocale('pt-br', {
	weekdays: ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'],
	weekdaysShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
	weekdaysMin: ['Do', 'Se', 'Te', 'Qa', 'Qi', 'Sx', 'Sa'],
	months: [
		'Janeiro',
		'Fevereiro',
		'Março',
		'Abril',
		'Maio',
		'Junho',
		'Julho',
		'Agosto',
		'Setembro',
		'Outubro',
		'Novembro',
		'Dezembro',
	],
})

interface CustomDatePickerProps extends React.ComponentProps<typeof DatePicker> {
	isWarning?: boolean
}

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({ isWarning = false, ...props }) => {
	return (
		<LocalizationProvider
			dateAdapter={AdapterDayjs}
			adapterLocale='pt-br'
			localeText={ptBR.components.MuiLocalizationProvider.defaultProps.localeText}
		>
			<div onClick={e => e.stopPropagation()}>
				<DatePicker
					format='DD/MM/YYYY'
					// referenceDate: quando value é null, o MUI usa esta data como base para o input por secções (dd/mm/aaaa).
					// Sem isto, ao começar a escrever uma secção o campo volta ao placeholder porque não tem contexto.
					referenceDate={dayjs()}
					slotProps={{
						textField: {
							sx: {
								borderRadius: 0,
								borderColor: isWarning ? 'var(--warning)' : 'transparent',
								transition: 'border-color 0.2s',
								'& .MuiInputBase-root': {
									width: '111px',
									color: isWarning ? 'var(--warning)' : 'var(--primary-dark)',
									padding: 0,
									'& fieldset': {
										borderRadius: 0,
										borderColor: 'transparent',
									},
								},
								'& input': {
									width: '85px',
									color: isWarning ? 'var(--warning)' : 'var(--primary-dark)',
									backgroundColor: 'rgba(245, 247, 252, 0.5)',
									fontFamily: 'Poppins',
									fontSize: '0.75rem',
									padding: '1.375px 4px',
								},
								'& .MuiInputAdornment-root': {
									width: '20px',
								},
								'& .MuiInputAdornment-positionEnd': {
									margin: 0,
								},
								'& button': {
									color: 'var(--primary)',
									backgroundColor: 'rgba(245, 247, 252, 0.5)',
									borderRadius: 0,
									minWidth: 0,
									minHeight: 0,
									width: '20px',
									height: '20px',
									padding: 0,
									margin: 0,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									boxSizing: 'border-box',
								},
								'& button svg': {
									width: '14px',
									height: '14px',
								},
								'& .MuiOutlinedInput-root fieldset': {
									borderRadius: 0,
									borderColor: 'transparent',
								},
								'& .MuiOutlinedInput-root:hover fieldset': {
									borderColor: 'transparent',
								},
								'& .MuiOutlinedInput-root.Mui-focused fieldset': {
									borderColor: 'transparent',
								},
								'& .MuiOutlinedInput-root.Mui-disabled fieldset': {
									borderColor: 'transparent',
								},
								'& .MuiOutlinedInput-root.Mui-error fieldset': {
									borderColor: 'transparent',
								},
								'&:hover': {
									borderColor: 'transparent',
									color: isWarning ? 'var(--warning)' : 'var(--primary-dark)',
								},
								'&.Mui-focused': {
									borderColor: 'transparent',
									color: isWarning ? 'var(--warning)' : 'var(--primary-dark)',
								},
								'&.Mui-disabled': {
									borderColor: 'transparent',
								},
								'&.Mui-error': {
									borderColor: 'transparent',
									color: isWarning ? 'var(--warning)' : 'var(--primary-dark)',
								},
							},
						},
						leftArrowIcon: {
							sx: {
								color: 'var(--primary)',
							},
						},
						rightArrowIcon: {
							sx: {
								color: 'var(--primary)',
							},
						},
						yearButton: {
							sx: {
								color: 'var(--primary-dark)',
								fontFamily: 'Poppins',
								'&.Mui-selected': {
									backgroundColor: 'var(--primary)',
								},
							},
						},
						day: {
							sx: {
								color: 'var(--primary-dark)',
								fontFamily: 'Poppins',
								'&.Mui-selected': {
									backgroundColor: 'var(--primary)',
								},
								'&.MuiPickersDay-today': {
									borderColor: 'var(--primary)',
								},
							},
						},
						calendarHeader: {
							sx: {
								color: 'var(--primary-dark)',
								fontFamily: 'Poppins',
							},
						},
						switchViewButton: {
							sx: {
								color: 'var(--primary)',
							},
						},
					}}
					{...props}
				/>
			</div>
		</LocalizationProvider>
	)
}

export default CustomDatePicker
